/**
 * THE TWO CARDS.
 *
 * The screen the board asked for: after signing in, choose which half of the
 * company you have come to work in.
 *
 * Shown ONCE. The answer is remembered per browser and the header carries a
 * switcher, so nobody meets this twice unless they clear it deliberately.
 * Anybody entitled to only one side never sees it at all - see landingFor()
 * in lib/modules.ts.
 *
 * DESIGN NOTES, because "make it beautiful" is not a specification:
 *
 *   * Two cards, side by side from the medium breakpoint, stacked on a phone.
 *     Equal height, so neither looks like the afterthought.
 *   * Each card carries its own colour - because on a site tablet in daylight,
 *     colour is read before text. Since the 30-09-2026 branding: Materials in
 *     the brand emerald, Service in graphite (was blue and amber; both
 *     colours are retired by the Board's palette).
 *   * A locked card is NOT hidden. A Project Manager who can open both sees
 *     two live cards; a store-man never reaches this screen. But if a role is
 *     ever added to one list and not the other, the reason appears on the card
 *     rather than the card silently vanishing.
 *   * Big touch targets. This is used on a phone, one-handed, outdoors.
 *   * No data is fetched here. It renders instantly on a 2G connection at a
 *     site, which is when first impressions are actually formed.
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, HardHat, Landmark, Lock, Package } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '@/lib/auth'
import { ROLE_LABEL } from '@/lib/domain'
import { MODULES, mayUseModule, modulesFor, rememberModule, type ModuleKey } from '@/lib/modules'

const LOOK: Record<ModuleKey, {
  icon: typeof Package
  ring: string; wash: string; badge: string; blob: string; go: string
  points: string[]
}> = {
  materials: {
    icon: Package,
    ring: 'hover:border-oval-400 focus-visible:ring-oval-500',
    wash: 'from-oval-50 to-white',
    badge: 'bg-oval-100 text-oval-800',
    blob: 'bg-oval-600',
    go: 'text-oval-700',
    points: [
      'Site indents and purchase requisitions',
      'Purchase orders, goods receipts, stock',
      'Issues, returns and inter-site transfers',
    ],
  },
  service: {
    icon: HardHat,
    ring: 'hover:border-slate-400 focus-visible:ring-oval-400',
    wash: 'from-slate-100 to-white',
    badge: 'bg-slate-200 text-slate-800',
    blob: 'bg-sidebar',
    go: 'text-slate-700',
    points: [
      'Work orders for works contractors',
      'Measurement Book and variation orders',
      'Running account bills and payments',
    ],
  },
  // Project Finance, 01-10-2026. Emerald like Materials but deeper, so the
  // two money-and-goods cards are told apart at a glance.
  finance: {
    icon: Landmark,
    ring: 'hover:border-oval-500 focus-visible:ring-oval-600',
    wash: 'from-oval-100/60 to-white',
    badge: 'bg-oval-200 text-oval-900',
    blob: 'bg-oval-800',
    go: 'text-oval-800',
    points: [
      'Payment requisitions and their approval',
      'Payments released, with proof',
      'Profit or loss of every project',
    ],
  },
}

/**
 * The cards this person is shown. A locked card says "not open to your role"
 * rather than vanishing - but only for the two original sides. Project
 * Finance (01-10-2026) is shown ONLY to those who may open it, so the screen
 * of everybody outside it stays exactly as it was.
 */
function cardsFor(role: Parameters<typeof mayUseModule>[0]) {
  return MODULES.filter((m) => m.key !== 'finance' || mayUseModule(role, m.key))
}

export default function ModulePicker() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const role = profile?.role ?? null

  // Somebody entitled to one side only should never be looking at this. If
  // they arrive here by typing the URL, send them where they belong rather
  // than showing them a choice they do not have.
  const mine = modulesFor(role)
  useEffect(() => {
    if (role && mine.length === 1) navigate(mine[0].home, { replace: true })
  }, [role, mine, navigate])

  if (!profile) return null

  function choose(key: ModuleKey, home: string) {
    rememberModule(key)
    navigate(home)
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ---------- masthead ---------- */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4 sm:px-6">
          <img src="/logo-oval.png" alt="OVAL" className="h-10 w-10 shrink-0 object-contain" />
          <div className="min-w-0">
            <p className="truncate text-base font-bold text-oval-800">OVAL IMS</p>
            <p className="truncate text-xs text-slate-500">
              OVAL Projects Engineering Limited
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="mb-8">
          <p className="text-sm text-slate-500">
            Signed in as <span className="font-semibold text-slate-800">{profile.full_name}</span>
            {' · '}{ROLE_LABEL[profile.role]}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-3xl">
            What have you come to do?
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            You can change sides at any time from the button in the top bar.
          </p>
        </div>

        {/* ---------- the two cards ---------- */}
        <div className={clsx('grid gap-4 sm:gap-5 md:grid-cols-2',
                             cardsFor(role).length > 2 && 'xl:grid-cols-3')}>
          {cardsFor(role).map((m) => {
            const look = LOOK[m.key]
            const allowed = mayUseModule(role, m.key)
            const Icon = look.icon

            if (!allowed) {
              return (
                <div key={m.key}
                     className="flex flex-col rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6">
                  <div className="flex items-start gap-3">
                    <span className="rounded-xl bg-slate-200 p-3 text-slate-400">
                      <Lock className="h-6 w-6" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-lg font-bold text-slate-500">{m.title}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Not open to {ROLE_LABEL[profile.role]}.
                      </p>
                    </div>
                  </div>
                  <p className="mt-4 text-xs leading-relaxed text-slate-400">
                    If you believe you should have this, ask the System Administrator.
                    It is decided by your role, not by this screen.
                  </p>
                </div>
              )
            }

            return (
              <button
                key={m.key}
                type="button"
                onClick={() => choose(m.key, m.home)}
                className={clsx(
                  'group flex flex-col rounded-2xl border-2 border-slate-200 bg-gradient-to-b p-6 text-left',
                  'shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg',
                  'focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2',
                  look.wash, look.ring,
                )}
              >
                <div className="flex items-start gap-3">
                  <span className={clsx('rounded-xl p-3 text-white shadow-sm', look.blob)}>
                    <Icon className="h-7 w-7" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-bold text-slate-900 sm:text-xl">{m.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{m.blurb}</p>
                  </div>
                </div>

                <ul className="mt-5 space-y-1.5">
                  {look.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className={clsx('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', look.blob)} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <span className={clsx(
                  'mt-6 inline-flex items-center gap-1.5 text-sm font-semibold', look.go,
                )}>
                  Open
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            )
          })}
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">
          Both sides share one login, one vendor master and one payables ledger.
          Nothing you do on one side changes the other.
        </p>
      </div>
    </div>
  )
}
